<template>
  <div
    class="el-table"
    :class="[
      {
        'el-table--fit': fit,
        'el-table--striped': stripe,
        'el-table--border': border || isGroup,
        'el-table--hidden': isHidden,
        'el-table--group': isGroup,
        'el-table--fluid-height': maxHeight,
        'el-table--scrollable-x': layout.scrollX,
        'el-table--scrollable-y': layout.scrollY,
        'el-table--enable-row-hover': !store.states.isComplex,
        'el-table--enable-row-transition': (store.states.data || []).length !== 0 && (store.states.data || []).length < 100,
        'el-table-sticky': isSticky
      },
      tableSize ? `el-table--${tableSize}` : ''
    ]"
    @mouseleave="handleMouseLeave($event)"
  >
    <div class="hidden-columns" ref="hiddenColumns">
      <slot></slot>
    </div>
    <div v-if="showHeader" v-mousewheel-event="handleHeaderFooterMousewheel" class="el-table__header-wrapper" ref="headerWrapper">
      <table-header
        ref="tableHeader"
        :store="store"
        :border="border"
        :default-sort="defaultSort"
        :style="{
          width: layout.bodyWidth ? layout.bodyWidth + 'px' : ''
        }"
      >
      </table-header>
    </div>
    <div
      v-if="useVirtulScroll"
      ref="virtualBox"
      @scroll.passive="virtualBoxScrollHandler"
      :style="{
        position: 'absolute',
        top: unitSize(layout.headerHeight),
        left: '0px',
        right: '0px',
        bottom: showSummary ? this.unitSize(layout.footerHeight) : '0px',
        overflow: 'auto'
      }"
    >
      <div ref="virtualBoxInner"></div>
    </div>
    <div class="el-table__body-wrapper" ref="bodyWrapper" :class="[layout.scrollX ? `is-scrolling-left` : 'is-scrolling-none']" :style="[bodyHeight]">
      <virtual-wrapper ref="virtualWrapper" fixed-position="main">
        <table-body
          :context="context"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
          :highlight="highlightCurrentRow"
          :style="{
            width: bodyWidth
          }"
        >
        </table-body>
        <div v-if="$slots.append" class="el-table__append-wrapper" ref="appendWrapper">
          <slot name="append"></slot>
        </div>
      </virtual-wrapper>
      <div v-if="!data || data.length === 0" class="el-table__empty-block" ref="emptyBlock" :style="emptyBlockStyle">
        <span class="el-table__empty-text">
          <slot name="empty">{{ emptyText || t("el.table.emptyText") }}</slot>
        </span>
      </div>
    </div>
    <div v-if="showSummary" v-mousewheel-event="handleHeaderFooterMousewheel" class="el-table__footer-wrapper" ref="footerWrapper">
      <table-footer
        :store="store"
        :border="border"
        :sum-text="sumText || t('el.table.sumText')"
        :summary-method="summaryMethod"
        :default-sort="defaultSort"
        :style="{
          width: layout.bodyWidth ? layout.bodyWidth + 'px' : ''
        }"
      >
      </table-footer>
    </div>
    <!--左侧固定区域-->
    <div v-if="fixedColumns.length > 0" v-mousewheel-event="handleFixedMousewheel" class="el-table__fixed" ref="fixedWrapper" :style="leftFixedWrapperStyle">
      <div v-if="showHeader" class="el-table__fixed-header-wrapper" ref="fixedHeaderWrapper">
        <table-header
          ref="fixedTableHeader"
          fixed="left"
          :border="border"
          :store="store"
          :style="{
            width: bodyWidth
          }"
        ></table-header>
      </div>
      <template v-if="!isSticky">
        <div
          class="el-table__fixed-body-wrapper"
          ref="fixedBodyWrapper"
          :style="[
            {
              top: layout.headerHeight + 'px'
            },
            fixedBodyHeight
          ]"
        >
          <virtual-wrapper ref="fixedVirtualWrapper" fixed-position="left">
            <table-body
              fixed="left"
              :store="store"
              :stripe="stripe"
              :highlight="highlightCurrentRow"
              :row-class-name="rowClassName"
              :row-style="rowStyle"
              :style="{
                width: bodyWidth
              }"
            >
            </table-body>
          </virtual-wrapper>
          <div v-if="$slots.append" class="el-table__append-gutter" :style="{ height: layout.appendHeight + 'px' }"></div>
        </div>
      </template>
    </div>
    <!--右侧固定区域-->
    <div v-if="rightFixedColumns.length > 0" v-mousewheel-event="handleFixedMousewheel" class="el-table__fixed-right" ref="rightFixedWrapper" :style="rightFixedWrapperStyle">
      <div v-if="showHeader" class="el-table__fixed-header-wrapper" ref="rightFixedHeaderWrapper">
        <table-header
          ref="rightFixedTableHeader"
          fixed="right"
          :border="border"
          :store="store"
          :style="{
            width: bodyWidth
          }"
        ></table-header>
      </div>
      <template v-if="!isSticky">
        <div
          class="el-table__fixed-body-wrapper"
          ref="rightFixedBodyWrapper"
          :style="[
            {
              top: layout.headerHeight + 'px'
            },
            fixedBodyHeight
          ]"
        >
          <virtual-wrapper ref="fixedVirtualWrapper" fixed-position="right">
            <table-body
              fixed="right"
              :store="store"
              :stripe="stripe"
              :row-class-name="rowClassName"
              :row-style="rowStyle"
              :highlight="highlightCurrentRow"
              :style="{
                width: bodyWidth
              }"
            >
            </table-body>
          </virtual-wrapper>
          <div v-if="$slots.append" class="el-table__append-gutter" :style="{ height: layout.appendHeight + 'px' }"></div>
        </div>
      </template>
    </div>

    <!--页脚统计行固定左侧区域-->
    <div v-if="showSummary" class="el-table__fixed_footer" :style="leftFixedWrapperFooterStyle">
      <div v-show="data && data.length > 0" class="el-table__fixed-footer-wrapper" ref="fixedFooterWrapper" style="height: 100%">
        <table-footer
          fixed="left"
          :border="border"
          :sum-text="sumText || t('el.table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth,
            height: '100%'
          }"
        >
        </table-footer>
      </div>
    </div>
    <!--页脚统计行固定右侧区域-->
    <div v-if="showSummary" class="el-table__fixed_footer el-table__fixed_footer-right" :style="rightFixedWrapperFooterStyle">
      <div
        v-show="data && data.length > 0"
        class="el-table__fixed-footer-wrapper"
        ref="rightFixedFooterWrapper"
        :style="{
          height: '100%',
          left: 'auto',
          right: rightFixedWrapperFooterOffset
        }"
      >
        <table-footer
          fixed="right"
          :border="border"
          :sum-text="sumText || t('el.table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            right: '0px',
            width: bodyWidth,
            height: '100%'
          }"
        ></table-footer>
      </div>
      <div
        v-if="rightFixedColumns.length > 0"
        class="el-table__fixed-footer-right-patch"
        :style="{
          right: '0px',
          width: layout.scrollY ? layout.gutterWidth + 'px' : '0',
          height: layout.footerHeight + 'px'
        }"
      ></div>
    </div>
    <div
      v-if="rightFixedColumns.length > 0"
      class="el-table__fixed-right-patch"
      ref="rightFixedPatch"
      :style="{
        width: layout.scrollY ? layout.gutterWidth + 'px' : '0',
        height: layout.headerHeight + 'px'
      }"
    ></div>
    <div class="el-table__column-resize-proxy" ref="resizeProxy" v-show="resizeProxyVisible"></div>
  </div>
</template>
<script>
import virtualWrapper from "./virtual";
import dsfElTableBody from "./body";
export default {
  name: "DsfElTable",
  mixins: [ELEMENT.Table],
  components: { virtualWrapper, TableBody: dsfElTableBody },
  directives: {
    MousewheelEvent: {
      bind(el, binding) {
        dsf.event.removeMouseWheelEvent(el, binding.value);
        dsf.event.addMouseWheelEvent(el, binding.value);
      },
      unbind(el, binding) {
        // console.log(binding.value==binding.oldValue)
        binding.oldValue && dsf.event.removeMouseWheelEvent(el, binding.oldValue);
      }
    }
  },
  provide() {
    return {
      $dsfElTable: this
    };
  },
  data() {
    this.tbodys = {};
    this.preStartIndex = 0;
    this.preStartColIndex = 0;
    this.scrollY = 0;
    this.scrollX = 0;
    this.currScrollPos = "";
    return {
      startColIndex: 0,
      endColIndex: 0,
      startIndex: 0,
      endIndex: 0,
      translateY: 0,
      rowHeights: [],
      fastScroll: false,
      virtualBoxWidth: 0
    };
  },
  props: {
    virtualScroll: {
      type: Boolean,
      default() {
        return false;
      }
    },
    virtualScrollRowHeight: {
      type: Number,
      default: 46
    }
  },
  computed: {
    isSticky() {
      let core = dsf.client.info.core;
      let coreVersion = dsf.client.info.coreVersion;
      if (core == "Chrome" && dsf.compareVersion(coreVersion, "63") >= 0) {
        return true;
      } else if (core.startsWith("Firefox") >= 0 && dsf.compareVersion(coreVersion, "72") >= 0) {
        return true;
      }
      return false;
    },
    minRowHeight() {
      return this.virtualScrollRowHeight;
    },
    useVirtulScroll() {
      //如果自适应高度属性为true且spanMethod为空
      return this.virtualScroll && this.height && this.height != "auto" && dsf.isUnDef(this.spanMethod);
    },
    scrollHeight() {
      return this.data.length * this.minRowHeight;
    },
    $translateY() {
      if (!this.useVirtulScroll) {
        return 0;
      }
      return this.translateY;
    },
    rightFixedWrapperStyle() {
      let size = {
        width: this.layout.rightFixedWidth ? this.layout.rightFixedWidth + "px" : "",
        right: this.layout.scrollY ? (this.border ? this.layout.gutterWidth : this.layout.gutterWidth || 0) + "px" : "",
        bottom: "0px"
      };
      return [size, this.fixedHeight];
    },
    leftFixedWrapperFooterStyle() {
      let size = {
        width: this.layout.fixedWidth ? this.layout.fixedWidth + "px" : "",
        height: this.unitSize(this.layout.footerHeight)
      };
      return size;
    },
    rightFixedWrapperFooterStyle() {
      let width = this.layout.rightFixedWidth + 1;
      if (this.layout.scrollY) {
        width += this.layout.gutterWidth;
      }
      let size = {
        width: this.unitSize(width),
        right: "0px", //this.layout.scrollY ? (this.border ? this.layout.gutterWidth : this.layout.gutterWidth || 0) + "px" : "",
        height: this.unitSize(this.layout.footerHeight)
      };
      return size;
    },
    rightFixedWrapperFooterOffset() {
      let right = 0;
      if (this.layout.scrollY) {
        right = this.unitSize(this.layout.gutterWidth);
      }
      return this.unitSize(right);
    },
    leftFixedWrapperStyle() {
      let size = {
        width: this.layout.fixedWidth ? this.layout.fixedWidth + "px" : ""
      };
      return [size, this.fixedHeight];
    },
    fixedHeight() {
      let style = {};
      if (this.isSticky) {
        style.height = this.unitSize(this.layout.headerHeight);
      } else {
        if (this.maxHeight) {
          if (this.showSummary) {
            return {
              bottom: 0
            };
          }
          style.bottom = this.layout.scrollX && this.data.length ? this.layout.gutterWidth + "px" : "";
        } else {
          if (this.showSummary) {
            let h = this.layout.tableHeight ? this.layout.tableHeight : 0;
            h -= this.layout.footerHeight;
            if (this.layout.scrollX) {
              h -= this.layout.gutterWidth - 1;
            }
            style.height = this.unitSize(h);
          } else {
            style.height = this.unitSize(this.layout.viewportHeight || 0);
          }
        }
      }
      return style;
    },
    emptyBlockStyle() {
      let style = ELEMENT.Table.computed.emptyBlockStyle.call(this);
      if (this.isSticky) {
        style.width = "100%";
        style.left = this.unitSize(0);
        style.position = "sticky";
      }
      return style;
    }
  },
  mounted() {
    // if(this.$refs.headerWrapper){
    //   headerWrapper
    //   dsf.event.addMouseWheelEvent()
    // }
  },
  beforeDestroy() {
    delete this.startColIndex;
    delete this.endColIndex;
    delete this.startIndex;
    delete this.endIndex;
    delete this.preStartIndex;
    delete this.preStartColIndex;
    delete this.scrollX;
    delete this.scrollY;
  },
  destroyed() {
    delete this.tbodys;
  },
  beforeUpdate() {},
  updated() {
    if (this.useVirtulScroll) {
      let bodyWrapper = this.$refs.bodyWrapper;
      if (bodyWrapper) {
        let width = 0;
        // this.virtualBoxWidth = 0;
        // 标识表格横向撑满屏幕,但存在纵向滚动条
        if (this.layout.scrollY && !this.layout.scrollX) {
          width = this.layout.bodyWidth - this.layout.gutterWidth;
        } else {
          width = this.layout.bodyWidth;
        }
        this.$refs.virtualBoxInner.style.width = this.unitSize(width);
        this.$refs.virtualBoxInner.style.height = this.unitSize(this.scrollHeight);
        bodyWrapper.style.overflow = "hidden";
        if (this.layout.scrollY) {
          bodyWrapper.style.width = `calc(100% - ${this.unitSize(this.layout.gutterWidth)})`;
        } else {
          bodyWrapper.style.width = "100%";
        }
        if (this.layout.scrollX) {
          bodyWrapper.style.height = `calc(${this.unitSize(this.layout.bodyHeight)} -  ${this.unitSize(this.layout.gutterWidth)})`;
        } else {
          bodyWrapper.style.height = this.unitSize(this.layout.bodyHeight);
        }
      }
    }
  },
  created() {
    this._doLayout = ELEMENT.Table.methods.doLayout;
    this._handleFixedMousewheel = ELEMENT.Table.methods.handleFixedMousewheel;
    this.layout.updateScrollY = function () {
      const height = this.height;
      if (height === null) return false;
      const bodyWrapper = this.table.bodyWrapper;
      let body = null;
      if (this.table.$el && bodyWrapper) {
        if (this.table.useVirtulScroll) {
          body = bodyWrapper.querySelector(".el-table__virtual-wrapper");
        } else {
          body = bodyWrapper.querySelector(".el-table__body");
        }
        const prevScrollY = this.scrollY;
        const scrollY = body.offsetHeight > this.bodyHeight;
        this.scrollY = scrollY;
        return prevScrollY !== scrollY;
      }
      return false;
    };

    this.scrollUpdateData = _.debounce(function (evt) {
      this.preStartIndex = this.startIndex;
      this.getViewVisableData();
      this.preStartIndex = this.startIndex;
      this.$emit("virtual-scroll", evt);
    }, 16);
  },
  methods: {
    handleFixedMousewheel(event, data) {
      if (this.useVirtulScroll) {
        const virtualBox = this.$refs.virtualBox;
        if (Math.abs(data.spinY) > 0) {
          const currentScrollTop = virtualBox.scrollTop;
          if (data.pixelY < 0 && currentScrollTop !== 0) {
            event.preventDefault();
          }
          if (data.pixelY > 0 && virtualBox.scrollHeight - virtualBox.clientHeight > currentScrollTop) {
            event.preventDefault();
          }
          virtualBox.scrollTop += Math.ceil(data.pixelY / 5);
        } else {
          virtualBox.scrollLeft += Math.ceil(data.pixelX / 5);
        }
      } else {
        this._handleFixedMousewheel.call(this, event, data);
      }
    },
    splitTableFooter() {
      // if (this.isSticky) {
      //   //todo
      // } else {
      //   return ELEMENT.Table.methods.splitTableFooter();
      // }
    },
    setFixelSize() {},
    getFixed(fixed) {
      if (fixed == "left" || fixed == "right") {
        return fixed;
      } else if (fixed === true) {
        return "left";
      } else {
        return "main";
      }
    },
    doLayout(isSyncRowHeight = true) {
      return new Promise((resolve) => {
        this._doLayout();
        if (isSyncRowHeight) {
          this.doLayoutUpdateRowHeight();
        }
        // this.updateBodyWrapSize();
        if (this.useVirtulScroll && this.data.length) {
          this.getViewVisableData();
          this.$nextTick(() => {
            this.resetVirtualWrapperTranslateY();
            this._doLayout();
            resolve();
          });
        } else {
          resolve();
        }
      });
    },
    virtualBoxScrollHandler(evt) {
      let scrollTop = evt.target.scrollTop;
      let scrollLeft = evt.target.scrollLeft;
      this.$refs.bodyWrapper.scrollTo(scrollLeft, scrollTop);
      if (this.$refs.fixedBodyWrapper) {
        this.$refs.fixedBodyWrapper.scrollTop = scrollTop;
      }
      if (this.$refs.rightFixedBodyWrapper) {
        this.$refs.rightFixedBodyWrapper.scrollTop = scrollTop;
      }
      // if (this.scrollY == scrollTop) {
      //   return;
      // }
      if (this.scrollY != scrollTop) {
        this.currScrollPos = "y";
      } else if (this.scrollX != scrollLeft) {
        this.currScrollPos = "x";
      }
      this.scrollY = scrollTop;
      this.scrollX = scrollLeft;
      if (this.useVirtulScroll) {
        this.$nextTick(() => {
          if (this.currScrollPos == "y") {
            this.preStartIndex = this.startIndex;
            this.getViewVisableData();
            this.translateY = this.setTranslateY();
            const differ = Math.abs(this.startIndex - this.preStartIndex);
            this.preStartIndex = this.startIndex;
            if (differ > 6) {
              this.fastScroll = true;
            }
          } else {
            this.preStartColIndex = this.startColIndex;
            this.getViewVisableData();
            const differ = Math.abs(this.startColIndex - this.preStartColIndex);
            this.preStartColIndex = this.startColIndex;
            if (differ > 2) {
              this.fastScroll = true;
            }
          }
          this.$emit("virtual-scroll", evt);
          this.debounceScrollEnded(evt);
        });
      }
    },
    debounceScrollEnded(evt) {
      const scrollingResetTimeInterval = 200;
      if (this.disablePointerEventsTimeoutId) {
        this.cancelAnimationTimeout(this.disablePointerEventsTimeoutId);
      }
      this.requestAnimationTimeout(() => {
        this.debounceScrollEndedCallback(evt);
      }, scrollingResetTimeInterval);
    },
    cancelAnimationTimeout(clearId) {
      window.cancelAnimationFrame(clearId);
    },
    requestAnimationTimeout(callback, delay) {
      let start;
      Promise.resolve().then(() => {
        start = Date.now();
      });
      const raf = window.requestAnimationFrame;
      const timeout = () => {
        //如果当前帧还有空余时间则执行渲染，没有则等待下一帧执行
        if (Date.now() - start >= delay) {
          callback.call();
        } else {
          this.disablePointerEventsTimeoutId = raf(timeout);
        }
      };
      this.disablePointerEventsTimeoutId = raf(timeout);
    },
    debounceScrollEndedCallback(evt) {
      this.disablePointerEventsTimeoutId = null;
      this.fastScroll = false;
    },
    doLayoutUpdateRowHeight() {
      if (this?.tbodys?.["main"]) {
        let body = this.tbodys["main"];
        body._syncRowHeight();
        return this.$nextTick();
      }
    },
    updateScrollHeight() {
      let sb = 0;
      _.each(this.rowPosition || [], (it) => {
        sb += it.height;
      });
      this.scrollHeight = sb;
    },
    getViewVisableData(boxRect) {
      let area = this.getViewVisableDataIndex(boxRect);
      this.preStartColIndex = this.startColIndex;
      this.preStartIndex = this.startIndex;
      this.startIndex = area.startIndex;
      this.endIndex = area.endIndex;
      this.startColIndex = area.startColIndex;
      this.endColIndex = area.endColIndex;
    },
    getViewVisableDataIndex(boxRect) {
      let startIndex, endIndex;
      if (!boxRect) {
        let bodyWrapper = this.$refs.bodyWrapper;
        let box = bodyWrapper?.getBoundingClientRect?.();
        if (box) {
          boxRect = {
            top: 0,
            left: 0,
            width: box.width,
            height: box.height,
            scrollLeft: bodyWrapper.scrollLeft || 0,
            scrollTop: bodyWrapper.scrollTop || 0
          };
        } else {
          boxRect = { top: 0, left: 0, width: 0, height: 0, scrollTop: 0, scrollLeft: 0 };
        }
      }
      let top = boxRect.top - 10;
      let bottom = boxRect.top + boxRect.height + 10;
      let offset = 0;
      let min = 0,
        max = 0;
      let first = false;
      for (let i = 0; i < this.data.length; i++) {
        let rowTop = offset;
        let rowBottom = rowTop + this.minRowHeight;
        rowTop -= boxRect?.scrollTop || 0;
        rowBottom -= boxRect?.scrollTop || 0;
        offset += this.minRowHeight;
        if (rowTop <= bottom + 10 && rowBottom >= top - 10) {
          if (!first) {
            first = true;
            min = i;
          }
          max = i;
        }
      }
      startIndex = min;
      endIndex = max;

      min = 0;
      max = 0;
      let left = boxRect.left - 10;
      let right = boxRect.left + boxRect.width + 10;
      first = false;
      offset = 0;
      let startColIndex = min;
      let endColIndex = max;
      for (let i = 0; i < this.columns.length; i++) {
        let column = this.columns[i];
        let w = column.realWidth || column.width || 0;
        let columnLeft = offset;
        let columnRight = columnLeft + w;
        columnLeft -= boxRect?.scrollLeft || 0;
        columnRight -= boxRect?.scrollLeft || 0;
        offset += w;
        if (columnLeft <= right + 10 && columnRight >= left - 10) {
          if (!first) {
            first = true;
            min = i;
          }
          max = i;
        }
      }
      startColIndex = min;
      endColIndex = max;

      return {
        startColIndex,
        endColIndex,
        startIndex,
        endIndex
      };
    },
    setTranslateY() {
      let y = 0;
      let startIndex = this.startIndex;
      y = startIndex * this.minRowHeight;
      return y;
    },
    resetVirtualWrapperTranslateY() {
      this?.$refs?.fixedVirtualWrapper?.changeTranslateY?.();
    },
  },
  watch: {
    // "$system.setting.fontSize": {
    //   handler(v, ov) {
    //     debugger;
    //   }
    // },
    data: {
      immediate: true,
      handler(v, ov) {
        if (this.useVirtulScroll) {
          this.$nextTick(() => {
            this.getViewVisableData();
          });
        }
      }
    }
  }
};
</script>




